import { Calculation } from "@/services/backend/rlt/calculations/types/calculation";
import { ReactNode, useState } from "react";
import { Selection } from "@/services/backend/rlt/calculations/types/expenses";
import {
  fHours,
  fMoney,
  IntNumberInput,
  onBeforeInputConvertToInt,
} from "@/routes/processes/_components/ui/number-input";
import {
  UpdateEmployeesRequest,
  UpdateExpensesRequest,
  UpdateParametersRequest,
  UpdateTravelTimeRequest,
  useRltCalculationShowQuery,
  useRltCalculationUpdateEmployeesMutation,
  useRltCalculationUpdateExpensesMutation,
  useRltCalculationUpdateParametersMutation,
  useRltCalculationUpdateTravelTimeMutation,
} from "@/services/backend/rlt/calculations/api/calculation-api";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useParams } from "react-router";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { Label } from "@/shared/components/ui/label";
import { Card } from "@/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/shared/components/ui/table";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";
import { tableInputClass } from "@/shared/components/tables/table-input-class";

export function CalculationCalculationRoute() {
  const { calculationId } = useParams();

  return <CalculationComponent calculationId={calculationId!} />;
}

export function CalculationComponent({
  calculationId,
  className,
  defaultOpen = true,
  disabled = false,
}: {
  calculationId: string;
  className?: string;
  defaultOpen?: boolean;
  disabled?: boolean;
}) {
  const {
    data: calculation,
    isLoading,
    error,
  } = useRltCalculationShowQuery({ id: calculationId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  return (
    <Card className={cn("gap-0 py-0", className)}>
      <EmployeeCount
        calculationId={calculation!.id}
        count={calculation!.employees}
        disabled={disabled}
      />
      <TreatmentPrices
        calculation={calculation!}
        defaultOpen={defaultOpen}
        disabled={disabled}
      />
      <MaterialCosts
        calculation={calculation!}
        defaultOpen={defaultOpen}
        disabled={disabled}
      />
      <TravelTimeCosts
        calculation={calculation!}
        defaultOpen={defaultOpen}
        disabled={disabled}
      />
      <ExpensesCosts
        calculation={calculation!}
        defaultOpen={defaultOpen}
        disabled={disabled}
      />
      <div className="flex justify-between rounded-b-lg border-t bg-gray-50 px-6 py-3">
        <span className="font-bold uppercase">{t("Gesamt")}</span>
        <span className="font-bold">{fMoney(calculation!.totalCost)}</span>
      </div>
    </Card>
  );
}

function EmployeeCount(props: {
  calculationId: string;
  count: number;
  disabled?: boolean;
}) {
  const { calculationId, count, disabled = false } = props;
  const [request, setRequest] = useState<UpdateEmployeesRequest>({
    id: calculationId,
    count,
  });
  const mutation = useRltCalculationUpdateEmployeesMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: calculationId,
    count,
  });

  return (
    <div className="flex justify-between px-6 py-3">
      <span>{t("Anzahl Mitarbeiter")}</span>
      <IntNumberInput
        value={request.count}
        onChange={(newCount) => setRequest({ ...request, count: newCount })}
        disabled={disabled}
      />
    </div>
  );
}

function TreatmentPrices({
  calculation,
  defaultOpen,
  disabled = false,
}: {
  calculation: Calculation;
  defaultOpen?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const { treatmentFinalPrice } = calculation;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border-t bg-gray-50 px-6 py-3">
        <div className="flex justify-between font-bold">
          <CollapsibleTrigger>
            <div className="flex items-center space-x-2">
              {open ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
              <span>{t("Grundbehandlungskosten")}</span>
            </div>
          </CollapsibleTrigger>
          <span className="">{fMoney(treatmentFinalPrice)}</span>
        </div>
      </div>
      <CollapsibleContent>
        <div className="border-t px-4 py-2">
          <TreatmentCostForWorkHours
            calculation={calculation}
            disabled={disabled}
          />
          <TreatmentCostExtras calculation={calculation} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function TreatmentCostForWorkHours({
  calculation,
  disabled = false,
}: {
  calculation: Calculation;
  disabled?: boolean;
}) {
  const { totalCleanTime, treatmentHourBasedPrices, parameters } = calculation;
  const [request, setRequest] = useState<UpdateParametersRequest>({
    id: calculation.id,
    parameters: calculation.parameters,
  });
  const mutation = useRltCalculationUpdateParametersMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: calculation.id,
    parameters: calculation.parameters,
  });

  return (
    <Table className="text-base">
      <TableBody className="[&_tr:last-child]:border-t">
        <TableRow className="border-none [&>td]:py-0.25">
          <TableCell>{t("Arbeitszeit aus Aufnahme")}</TableCell>
          <TableCell className="text-right">
            <span className="px-2">{fHours(totalCleanTime)}</span>
            <span className="ml-1">h</span>
          </TableCell>
        </TableRow>
        <TableRow className="border-none [&>td]:py-0.25">
          <TableCell>{t("Manntage")}</TableCell>
          <TableCell className="text-right">
            <span className="px-2">
              {fHours(totalCleanTime / parameters.hoursInManDay)}
            </span>
            <span className="ml-1">T</span>
          </TableCell>
        </TableRow>
        <TableRow className="border-none [&>td]:py-0.25">
          <TableCell>{t("Tagessatz")}</TableCell>
          <TableCell className="text-right">
            <Input
              type="number"
              className={cn(tableInputClass, "inline-flex w-20")}
              value={request.parameters.manDayRate}
              onChange={(e) =>
                setRequest({
                  ...request,
                  parameters: {
                    ...request.parameters,
                    manDayRate: Number(e.target.value),
                  },
                })
              }
              disabled={disabled}
            />
            <span className="ml-1">€</span>
          </TableCell>
        </TableRow>
        {treatmentHourBasedPrices.totalBasePrice !==
          treatmentHourBasedPrices.totalFinalPrice && (
          <TableRow className="border-none font-bold [&>td]:py-0.5">
            <TableCell>{t("Grundbehandlungskosten Arbeitszeit")}</TableCell>
            <TableCell className="text-right">
              <span className="px-2">
                {fHours(treatmentHourBasedPrices.totalBasePrice)}
              </span>
              <span className="ml-1">€</span>
            </TableCell>
          </TableRow>
        )}
        {treatmentHourBasedPrices.totalCycleDiscount > 0 && (
          <TableRow className="border-none [&>td]:py-0.5">
            <TableCell>{t("Rabatt für Folgebehandlung")}</TableCell>
            <TableCell className="text-right">
              <span className="px-2">
                {fHours(treatmentHourBasedPrices.totalCycleDiscount)}
              </span>
              <span className="ml-1">€</span>
            </TableCell>
          </TableRow>
        )}
        {treatmentHourBasedPrices.totalSpecialDiscount > 0 && (
          <TableRow className="border-none [&>td]:py-0.5">
            <TableCell>{t("Sonderrabatte")}</TableCell>
            <TableCell className="text-right">
              <span className="px-2">
                {fHours(treatmentHourBasedPrices.totalSpecialDiscount)}
              </span>
              <span className="ml-1">€</span>
            </TableCell>
          </TableRow>
        )}
        <TableRow className="font-bold [&>td]:py-0.5">
          <TableCell>{t("Grundbehandlungskosten Arbeitszeit")}</TableCell>
          <TableCell className="text-right">
            <span className="px-2">
              {fHours(treatmentHourBasedPrices.totalFinalPrice)}
            </span>
            <span className="ml-1">€</span>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

function TreatmentCostExtras({ calculation }: { calculation: Calculation }) {
  const { treatmentExtrasPrices } = calculation;

  return (
    <Table className="text-base">
      <TableBody className="[&_tr:last-child]:border-t">
        <TableRow className="border-none font-bold [&>td]:py-0.5">
          <TableCell>{t("Grundbehandlungskosten Extras")}</TableCell>
          <TableCell className="text-right">
            <span className="px-2">
              {fHours(treatmentExtrasPrices.totalFinalPrice)}
            </span>
            <span className="ml-1">€</span>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

function MaterialCosts({
  calculation,
  defaultOpen,
  disabled = false,
}: {
  calculation: Calculation;
  defaultOpen?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [request, setRequest] = useState<UpdateParametersRequest>({
    id: calculation.id,
    parameters: calculation.parameters,
  });
  const mutation = useRltCalculationUpdateParametersMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: calculation.id,
    parameters: calculation.parameters,
  });

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border-t bg-gray-50 px-6 py-3">
        <div className="flex justify-between font-bold">
          <CollapsibleTrigger>
            <div className="flex items-center space-x-2">
              {open ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
              <span>{t("Materialkosten")}</span>
            </div>
          </CollapsibleTrigger>
          <span className="">{fMoney(calculation.materialCost)}</span>
        </div>
      </div>
      <CollapsibleContent className="px-3 py-2">
        <Table className="text-base">
          <TableBody>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Pauschale auf Grundbehandlung")}</TableCell>
              <TableCell className="text-right">
                <Input
                  type="number"
                  className={cn(tableInputClass, "inline-flex w-20")}
                  value={request.parameters.materialFlatRate}
                  onChange={(e) =>
                    setRequest({
                      ...request,
                      parameters: {
                        ...request.parameters,
                        materialFlatRate: Number(e.target.value),
                      },
                    })
                  }
                  disabled={disabled}
                />
                <span className="ml-1">%</span>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CollapsibleContent>
    </Collapsible>
  );
}

function TravelTimeCosts({
  calculation,
  defaultOpen,
  disabled = false,
}: {
  calculation: Calculation;
  defaultOpen?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [request, setRequest] = useState<UpdateTravelTimeRequest>({
    id: calculation.id,
    drives: calculation.travelTime.drives,
    hoursPerDrive: calculation.travelTime.hoursPerDrive,
  });
  const mutation = useRltCalculationUpdateTravelTimeMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: calculation.id,
    drives: calculation.travelTime.drives,
    hoursPerDrive: calculation.travelTime.hoursPerDrive,
  });

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border-t bg-gray-50 px-6 py-3">
        <div className="flex justify-between font-bold">
          <CollapsibleTrigger>
            <div className="flex items-center space-x-2">
              {open ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
              <span>{t("Fahrtkosten")}</span>
            </div>
          </CollapsibleTrigger>
          <span className="">{fMoney(calculation.travelTimeCost)}</span>
        </div>
      </div>
      <CollapsibleContent className="px-3 py-2">
        <Table className="text-base">
          <TableBody className="[&_tr:last-child]:border-t">
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Anzahl Fahrten")}</TableCell>
              <TableCell className="text-right">
                <Input
                  type="number"
                  className={cn(tableInputClass, "inline-flex w-20")}
                  value={request.drives}
                  onChange={(e) => {
                    setRequest({ ...request, drives: Number(e.target.value) });
                  }}
                  onBeforeInput={onBeforeInputConvertToInt}
                  disabled={disabled}
                />
                <span className="ml-1">#</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Dauer pro Fahrt")}</TableCell>
              <TableCell className="text-right">
                <Input
                  type="number"
                  className={cn(tableInputClass, "inline-flex w-20")}
                  value={request.hoursPerDrive}
                  onChange={(e) =>
                    setRequest({
                      ...request,
                      hoursPerDrive: Number(e.target.value),
                    })
                  }
                  disabled={disabled}
                />
                <span className="ml-1">h</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Anzahl Mitarbeiter")}</TableCell>
              <TableCell className="text-right">
                <span className="px-2">{calculation.travelTime.employees}</span>
                <span className="ml-1">#</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Fahrzeit Gesamt")}</TableCell>
              <TableCell className="text-right">
                <span className="px-2">
                  {fHours(calculation.travelTime.totalTravelTime)}
                </span>
                <span className="ml-1">h</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Manntage")}</TableCell>
              <TableCell className="text-right">
                <span className="px-2">
                  {fHours(
                    calculation.travelTime.totalTravelTime /
                      calculation.parameters.hoursInManDay,
                  )}
                </span>
                <span className="ml-1">h</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Manntagessatz")}</TableCell>
              <TableCell className="text-right">
                <span className="px-2">
                  {fHours(calculation.parameters.manDayRate)}
                </span>
                <span className="ml-1">€</span>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ExpensesCosts({
  calculation,
  defaultOpen,
  disabled = false,
}: {
  calculation: Calculation;
  defaultOpen?: boolean;
  disabled?: boolean;
}) {
  const { expenses } = calculation;
  const [open, setOpen] = useState(defaultOpen);

  const [request, setRequest] = useState<UpdateExpensesRequest>({
    id: calculation.id,
    selection: expenses.selection,
    perDiemDays: expenses.perDiem.days,
    overnightNights: expenses.overnight.nights,
  });
  const mutation = useRltCalculationUpdateExpensesMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    id: calculation.id,
    selection: expenses.selection,
    perDiemDays: expenses.perDiem.days,
    overnightNights: expenses.overnight.nights,
  });

  let selectionView: ReactNode;
  switch (expenses.selection) {
    case Selection.None:
      selectionView = <div />;
      break;
    case Selection.PerDiem:
      selectionView = (
        <Table className="text-base">
          <TableBody className="[&_tr:last-child]:border-t">
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Anzahl Tage")}</TableCell>
              <TableCell className="text-right">
                <Input
                  type="number"
                  className={cn(tableInputClass, "inline-flex w-20")}
                  value={request.perDiemDays}
                  onChange={(e) =>
                    setRequest({
                      ...request,
                      perDiemDays: Number(e.target.value),
                    })
                  }
                  onBeforeInput={onBeforeInputConvertToInt}
                  disabled={disabled}
                />
                <span className="ml-1">#</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Anzahl Mitarbeiter")}</TableCell>
              <TableCell className="text-right">
                <span className="px-2">
                  {fMoney(calculation.expenses.perDiem.employees)}
                </span>
                <span className="ml-1">#</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Verpflegungspauschale")}</TableCell>
              <TableCell className="text-right">
                <span className="px-2">
                  {fMoney(calculation.expenses.perDiem.allowance)}
                </span>
                <span className="ml-1">€</span>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      );
      break;
    case Selection.Overnight:
      selectionView = (
        <Table className="text-base">
          <TableBody className="[&_tr:last-child]:border-t">
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Anzahl Nächte")}</TableCell>
              <TableCell className="text-right">
                <Input
                  type="number"
                  className={cn(tableInputClass, "inline-flex w-20")}
                  value={request.overnightNights}
                  onChange={(e) =>
                    setRequest({
                      ...request,
                      overnightNights: Number(e.target.value),
                    })
                  }
                  onBeforeInput={onBeforeInputConvertToInt}
                  disabled={disabled}
                />
                <span className="ml-1">#</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Anzahl Mitarbeiter")}</TableCell>
              <TableCell className="text-right">
                <span className="px-2">
                  {fMoney(calculation.expenses.overnight.employees)}
                </span>
                <span className="ml-1">#</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Verpflegungspauschale")}</TableCell>
              <TableCell className="text-right">
                <span className="px-2">
                  {fMoney(calculation.expenses.overnight.allowance)}
                </span>
                <span className="ml-1">€</span>
              </TableCell>
            </TableRow>
            <TableRow className="border-none [&>td]:py-0.25">
              <TableCell>{t("Übernachtungskosten")}</TableCell>
              <TableCell className="text-right">
                <span className="px-2">
                  {fMoney(calculation.expenses.overnight.hotelRate)}
                </span>
                <span className="ml-1">€</span>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      );
      break;
    default:
      selectionView = <div />;
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border-t bg-gray-50 px-6 py-3">
        <div className="flex justify-between font-bold">
          <CollapsibleTrigger>
            <div className="flex items-center space-x-2">
              {open ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
              <span>{t("Spesen")}</span>
            </div>
          </CollapsibleTrigger>
          <span className="">{fMoney(calculation.expenses.total)}</span>
        </div>
      </div>
      <CollapsibleContent>
        <div className="space-y-2 border-t py-2">
          <RadioGroup
            className="flex justify-center space-x-2 px-3"
            value={request.selection}
            onValueChange={(selection) =>
              setRequest({ ...request, selection: selection as Selection })
            }
            disabled={disabled}
          >
            {[Selection.None, Selection.PerDiem, Selection.Overnight].map(
              (kind) => (
                <Label
                  key={kind}
                  htmlFor={kind}
                  className="flex w-full cursor-pointer items-center justify-center space-x-2 rounded border px-3 py-3"
                >
                  <RadioGroupItem value={kind} id={kind} />
                  <span>{t(kind)}</span>
                </Label>
              ),
            )}
          </RadioGroup>
          <div className="px-3">{selectionView}</div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
